import { Component } from '@theme/component';

/**
 * CustomMultiMenu - A custom element that manages multiple menus
 * Desktop: Mega menu with hover states
 * Mobile: Carousel with flyout navigation
 */
class CustomMultiMenu extends Component {
  #abortController = new AbortController();
  #currentMenu = null;
  #currentLevel2Menu = null;
  #touchStartX = 0;
  #touchEndX = 0;

  connectedCallback() {
    super.connectedCallback();

    // Set up dynamic columns from data attribute
    const columns = String(this.getAttribute('data-columns') || 5);
    this.style.setProperty('--menu-columns', columns);

    // Desktop-only menu
    this.#initializeDesktopMenu();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #isMobileView() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  /** @type {number | undefined} */
  #hoverTimeout = undefined;

  /** @type {HTMLElement | null} */
  #megamenuContainer = null;

  /** @type {boolean} */
  #megamenuMovedToBody = false;

  /**
   * Initialize desktop mega menu hover states
   */
  #initializeDesktopMenu() {
    const buttons = Array.from(this.querySelectorAll('.custom-multimenu__desktop-button'));
    const submenus = Array.from(this.querySelectorAll('.custom-multimenu__submenu'));
    this.#megamenuContainer = /** @type {HTMLElement | null} */ (this.querySelector('.custom-multimenu__megamenu-container'));

    // Move megamenu to body for true viewport positioning
    // Set all positioning via inline styles with !important for absolute bulletproof positioning
    if (this.#megamenuContainer && !this.#megamenuMovedToBody) {
      document.body.appendChild(this.#megamenuContainer);
      this.#megamenuMovedToBody = true;
      
      // Apply bulletproof inline styles with !important - overrides absolutely everything
      const style = this.#megamenuContainer.style;
      style.setProperty('position', 'absolute', 'important');
      style.setProperty('left', '0', 'important');
      style.setProperty('right', '0', 'important');
      style.setProperty('width', '100vw', 'important');
      style.setProperty('max-width', '100vw', 'important');
      style.setProperty('min-width', '100vw', 'important');
      style.setProperty('box-sizing', 'border-box', 'important');
      style.setProperty('margin', '0', 'important');
      style.setProperty('padding', '0', 'important');
      style.setProperty('z-index', '9999', 'important');
      style.setProperty('background', 'white', 'important');
      style.setProperty('display', 'none', 'important');
      style.setProperty('justify-content', 'center', 'important');
    }

    buttons.forEach((button) => {
      button.addEventListener(
        'mouseenter',
        () => {
          clearTimeout(this.#hoverTimeout);
          this.#activateDesktopMenu(/** @type {HTMLElement} */ (button));
        },
        { signal: this.#abortController.signal }
      );

      button.addEventListener(
        'focus',
        () => {
          clearTimeout(this.#hoverTimeout);
          this.#activateDesktopMenu(/** @type {HTMLElement} */ (button));
        },
        { signal: this.#abortController.signal }
      );

      button.addEventListener(
        'mouseleave',
        () => {
          // Delay deactivation to allow moving cursor to submenu
          this.#hoverTimeout = setTimeout(() => this.#deactivateDesktopMenu(), 300);
        },
        { signal: this.#abortController.signal }
      );
    });

    // Keep menu open when hovering over submenu container
    if (this.#megamenuContainer) {
      this.#megamenuContainer.addEventListener(
        'mouseenter',
        () => {
          clearTimeout(this.#hoverTimeout);
        },
        { signal: this.#abortController.signal }
      );

      this.#megamenuContainer.addEventListener(
        'mouseleave',
        () => {
          this.#deactivateDesktopMenu();
        },
        { signal: this.#abortController.signal }
      );
    }
  }

  /**
   * Activate desktop menu submenu
   * @param {HTMLElement} button
   */
  #activateDesktopMenu(button) {
    // Deactivate all first
    this.querySelectorAll('.custom-multimenu__desktop-button').forEach((btn) => {
      btn.setAttribute('aria-expanded', 'false');
    });
    if (this.#megamenuContainer) {
      this.#megamenuContainer.querySelectorAll('.custom-multimenu__submenu').forEach((submenu) => {
        submenu.classList.remove('is-active');
        /** @type {HTMLElement} */ (submenu).style.setProperty('display', 'none', 'important');
      });
    }

    // Activate selected
    button.setAttribute('aria-expanded', 'true');
    const submenuId = button.getAttribute('aria-controls');
    if (this.#megamenuContainer) {
      const submenu = /** @type {HTMLElement | null} */ (this.#megamenuContainer.querySelector(`#${submenuId}`));
      if (submenu) {
        submenu.classList.add('is-active');
        // Apply inline styles to submenu for bulletproof full width
        submenu.style.setProperty('display', 'block', 'important');
        submenu.style.setProperty('width', '100%', 'important');
        submenu.style.setProperty('max-width', 'none', 'important');
        submenu.style.setProperty('position', 'relative', 'important');
        submenu.style.setProperty('box-sizing', 'border-box', 'important');
        submenu.style.setProperty('margin-top', '0', 'important');
        
        // Also style the inner content wrapper
        const submenuContent = /** @type {HTMLElement | null} */ (submenu.querySelector('.custom-multimenu__submenu-content'));
        if (submenuContent) {
          submenuContent.style.setProperty('width', '100%', 'important');
          submenuContent.style.setProperty('max-width', '1800px', 'important');
          submenuContent.style.setProperty('margin', '0 auto', 'important');
          submenuContent.style.setProperty('padding', 'var(--menu-tray-padding, 24px) 32px', 'important');
          submenuContent.style.setProperty('box-sizing', 'border-box', 'important');
          
          // Also style the grid container inside
          const gridContent = /** @type {HTMLElement | null} */ (submenuContent.querySelector('.custom-multimenu__megamenu-content'));
          if (gridContent) {
            gridContent.style.setProperty('width', '100%', 'important');
            gridContent.style.setProperty('display', 'grid', 'important');
            gridContent.style.setProperty('gap', 'var(--menu-column-gap, 24px)', 'important');
          }
        }
      }
    }
    
    // Position and show container with inline styles for bulletproof positioning
    if (this.#megamenuContainer) {
      // Get position from header - add scrollY for absolute positioning relative to document
      const header = document.querySelector('header') || document.querySelector('.header');
      if (header) {
        const headerRect = header.getBoundingClientRect();
        const topPosition = headerRect.bottom + window.scrollY - 10;
        this.#megamenuContainer.style.top = `${topPosition}px`;
      }
      // Use inline styles with !important for display to guarantee they apply
      this.#megamenuContainer.style.setProperty('display', 'flex', 'important');
      this.#megamenuContainer.style.setProperty('margin-top', '9px', 'important');
      // Box shadow only on bottom/sides (clip the top)
      this.#megamenuContainer.style.setProperty('box-shadow', '0 4px 12px rgba(0, 0, 0, 0.15)', 'important');
      this.#megamenuContainer.style.setProperty('clip-path', 'inset(0 -20px -20px -20px)', 'important');
      this.#megamenuContainer.classList.add('is-visible');
    }
  }

  /**
   * Deactivate desktop menu
   */
  #deactivateDesktopMenu() {
    this.querySelectorAll('.custom-multimenu__desktop-button').forEach((btn) => {
      btn.setAttribute('aria-expanded', 'false');
    });
    if (this.#megamenuContainer) {
      this.#megamenuContainer.querySelectorAll('.custom-multimenu__submenu').forEach((submenu) => {
        submenu.classList.remove('is-active');
        /** @type {HTMLElement} */ (submenu).style.setProperty('display', 'none', 'important');
      });
      // Hide container with inline styles using !important
      this.#megamenuContainer.style.setProperty('display', 'none', 'important');
      this.#megamenuContainer.style.setProperty('box-shadow', 'none', 'important');
      this.#megamenuContainer.classList.remove('is-visible');
    }
  }

  /**
   * Initialize mobile menu with carousel and flyouts
   */
  #initializeMobileMenu() {
    const mobileButtons = Array.from(this.querySelectorAll('.custom-multimenu__mobile-button'));
    const flyout = this.querySelector('[ref="mobileFlyout"]');
    const flyoutBack = this.querySelector('[ref="flyoutBack"]');
    const flyoutLevel2 = this.querySelector('[ref="mobileFlyoutLevel2"]');
    const flyoutBackLevel2 = this.querySelector('[ref="flyoutBackLevel2"]');
    const menuDataScript = this.querySelector('[ref="menuData"]');

    if (!menuDataScript) return;

    /** @type {any} */
    let menuData;
    try {
      menuData = JSON.parse(menuDataScript.textContent || '{}');
    } catch (e) {
      console.error('Failed to parse menu data:', e);
      return;
    }

    // Add click handlers to carousel buttons
    mobileButtons.forEach((button) => {
      button.addEventListener(
        'click',
        () => this.#handleMobileMenuClick(/** @type {HTMLElement} */ (button), menuData, /** @type {HTMLElement} */ (flyout)),
        { signal: this.#abortController.signal }
      );
    });

    // Back button level 1
    if (flyoutBack) {
      flyoutBack.addEventListener(
        'click',
        () => this.#closeFlyout(/** @type {HTMLElement} */ (flyout)),
        { signal: this.#abortController.signal }
      );
    }

    // Back button level 2
    if (flyoutBackLevel2) {
      flyoutBackLevel2.addEventListener(
        'click',
        () => this.#closeFlyout(/** @type {HTMLElement} */ (flyoutLevel2)),
        { signal: this.#abortController.signal }
      );
    }

    // Close flyout on overlay click
    if (flyout) {
      flyout.addEventListener(
        'click',
        (e) => {
          if (e.target === flyout) {
            this.#closeFlyout(/** @type {HTMLElement} */ (flyout));
          }
        },
        { signal: this.#abortController.signal }
      );
    }
  }

  /**
   * Handle mobile menu button click - shows first level flyout
   * @param {HTMLElement} button
   * @param {any} menuData
   * @param {HTMLElement} flyout
   */
  #handleMobileMenuClick(button, menuData, flyout) {
    const menuHandle = button.getAttribute('data-menu-handle');
    if (!menuHandle) return;
    
    const menu = menuData.menus[menuHandle];

    if (!menu) return;

    const flyoutTitle = this.querySelector('[ref="flyoutTitle"]');
    const flyoutContent = this.querySelector('[ref="flyoutContent"]');

    if (flyoutTitle) {
      flyoutTitle.textContent = menu.title;
    }

    if (flyoutContent) {
      flyoutContent.innerHTML = '';

      const list = document.createElement('ul');
      list.className = 'custom-multimenu__flyout-list';

      menu.links.forEach((/** @type {any} */ link) => {
        const item = document.createElement('li');
        item.className = 'custom-multimenu__flyout-item';

        const linkEl = document.createElement('a');
        linkEl.className = 'custom-multimenu__flyout-link';
        linkEl.href = link.url;
        linkEl.textContent = link.title;

        item.appendChild(linkEl);

        // If item has children, add arrow and click handler
        if (link.children && link.children.length > 0) {
          const arrow = document.createElement('div');
          arrow.className = 'custom-multimenu__flyout-arrow';
          arrow.textContent = '›';

          item.appendChild(arrow);
          item.style.cursor = 'pointer';

          item.addEventListener(
            'click',
            (e) => {
              e.preventDefault();
              this.#showLevel2Flyout(link, menuData);
            },
            { signal: this.#abortController.signal }
          );
        }

        list.appendChild(item);
      });

      flyoutContent.appendChild(list);
    }

    this.#openFlyout(flyout);
  }

  /**
   * Show second level flyout with children
   * @param {any} link
   * @param {any} menuData
   */
  #showLevel2Flyout(link, menuData) {
    const flyoutLevel2 = this.querySelector('[ref="mobileFlyoutLevel2"]');
    const flyoutTitleLevel2 = this.querySelector('[ref="flyoutTitleLevel2"]');
    const flyoutContentLevel2 = this.querySelector('[ref="flyoutContentLevel2"]');

    if (flyoutTitleLevel2) {
      flyoutTitleLevel2.textContent = link.title;
    }

    if (flyoutContentLevel2) {
      flyoutContentLevel2.innerHTML = '';

      const list = document.createElement('ul');
      list.className = 'custom-multimenu__flyout-children-list';

      link.children.forEach((/** @type {any} */ child) => {
        const item = document.createElement('li');
        item.className = 'custom-multimenu__flyout-child-item';

        const linkEl = document.createElement('a');
        linkEl.className = 'custom-multimenu__flyout-child-link';
        linkEl.href = child.url;
        linkEl.textContent = child.title;

        item.appendChild(linkEl);
        list.appendChild(item);
      });

      flyoutContentLevel2.appendChild(list);
    }

    this.#openFlyout(/** @type {HTMLElement} */ (flyoutLevel2));
  }

  /**
   * Open a flyout menu
   * @param {HTMLElement | null} flyout
   */
  #openFlyout(flyout) {
    if (!flyout) return;
    flyout.removeAttribute('hidden');
    flyout.style.display = 'flex';
    document.body.style.overflow = 'hidden';
  }

  /**
   * Close a flyout menu
   * @param {HTMLElement | null} flyout
   */
  #closeFlyout(flyout) {
    if (!flyout) return;
    flyout.setAttribute('hidden', '');
    flyout.style.display = 'none';

    // Only restore if both flyouts are closed
    const flyoutLevel2 = this.querySelector('[ref="mobileFlyoutLevel2"]');
    const mainFlyout = this.querySelector('[ref="mobileFlyout"]');

    if (
      mainFlyout &&
      mainFlyout.hasAttribute('hidden') &&
      flyoutLevel2 &&
      flyoutLevel2.hasAttribute('hidden')
    ) {
      document.body.style.overflow = '';
    }
  }
}

customElements.define('custom-multimenu', CustomMultiMenu);
